import { useCallback } from "react";
import { useFormHandler, useLoggedInAs, usePostHog } from "shared/hooks";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import {
  PeopleManySimpleDocument,
  PersonCheckIfExistsByEmailQuery,
  PersonCreateMutation,
  usePersonCreateMutation,
  PersonUpdateAddToVisibleToMutation,
  usePersonUpdateAddToVisibleToMutation,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { GraphQLFormattedError } from "graphql";
import { phoneNumberRegex } from "shared/utils";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.People]);

export const personCreateFormSchema = z
  .object({
    unknownEmail: z.boolean().optional(),
    email: z
      .string({
        required_error: `${i18n.t("people:PERSON_CREATE_MODAL.LABELS.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .trim()
      .min(6, {
        message: `${i18n.t("people:PERSON_CREATE_MODAL.LABELS.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .email({ message: i18n.t("core:FORM_ERRORS.INVALID_EMAIL") })
      .or(z.literal("email@example.com")),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z
      .string()
      .optional()
      .refine(
        value => {
          if (value) {
            // Look for valid phone numbers
            return phoneNumberRegex.test(value);
          }
          return true;
        },
        {
          message: i18n.t("people:PERSON_CREATE_MODAL.ERRORS.PHONE"),
        },
      ),
    phoneExt: z
      .string()
      .optional()
      .refine(
        value => {
          if (value) {
            // Look for valid phone numbers
            return /^\d{1,6}$/.test(value);
          }
          return true;
        },
        {
          message: i18n.t("people:PERSON_CREATE_MODAL.ERRORS.PHONE_EXT"),
        },
      ),
  })
  .superRefine((data, ctx) => {
    if (data.unknownEmail && !data.firstName && !data.lastName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t("people:PERSON_CREATE_MODAL.ERRORS.ONE_REQUIRED"),
        path: ["firstName"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t("people:PERSON_CREATE_MODAL.ERRORS.ONE_REQUIRED"),
        path: ["lastName"],
      });
      return false;
    }
  });

export type PersonCreateFormValues = z.infer<typeof personCreateFormSchema>;

type UsePersonCreateFormProps = {
  existingPersonInfo:
    | PersonCheckIfExistsByEmailQuery["personCheckIfExistsByEmail"]
    | null;
  onSuccess: (person: PersonCreateMutation["personCreate"]) => void;
  onError: (message: string) => void;
};

export const usePersonCreateForm = ({
  existingPersonInfo,
  onSuccess,
  onError,
}: UsePersonCreateFormProps) => {
  const { t } = useTranslation("people", { keyPrefix: "PERSON_CREATE_MODAL" });
  const loggedInAs = useLoggedInAs();

  const defaultValues: PersonCreateFormValues = {
    unknownEmail: false,
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
  };

  const { trackPostHogException } = usePostHog();

  // When creating a new person
  const [personCreate] = usePersonCreateMutation({
    refetchQueries: [PeopleManySimpleDocument],
  });

  // When ONLY adding a visibleTo entry to the person
  const [personUpdateAddVisibleTo] = usePersonUpdateAddToVisibleToMutation({
    refetchQueries: [PeopleManySimpleDocument],
  });

  return useFormHandler<PersonCreateFormValues>(
    useCallback(
      async data => {
        if (data && data.email && loggedInAs) {
          try {
            let personData:
              | PersonCreateMutation["personCreate"]
              | PersonUpdateAddToVisibleToMutation["personUpdateAddToVisibleTo"]
              | null = null;

            let error: GraphQLFormattedError[] | undefined;

            if (existingPersonInfo && existingPersonInfo.exists) {
              // The person already exists, so we just need to add the visibleTo entry
              const {
                data: personUpdateAddToVisibleToData,
                errors: personUpdateAddToVisibleToError,
              } = await personUpdateAddVisibleTo({
                variables: {
                  email: data.email,
                  entityId: loggedInAs.entityId,
                  type: loggedInAs.type,
                },
              });
              personData =
                personUpdateAddToVisibleToData?.personUpdateAddToVisibleTo;
              error = personUpdateAddToVisibleToError
                ? [...personUpdateAddToVisibleToError]
                : undefined;
            } else {
              // The person does not exist, so we need to create them
              const { data: personCrateData, errors: personCrateError } =
                await personCreate({
                  variables: {
                    entityId: loggedInAs.entityId,
                    type: loggedInAs.type,
                    record: {
                      email: data.email,
                      firstName: data.firstName ?? "",
                      lastName: data.lastName ?? "",
                      phone: data.phone ?? "",
                    },
                  },
                });
              personData = personCrateData?.personCreate;
              error = personCrateError ? [...personCrateError] : undefined;
            }

            if (personData && !error) {
              onSuccess(personData);
            } else {
              throw new Error(error?.map(e => e.message).join(", "));
            }
          } catch (error) {
            trackPostHogException(error, {
              fileName: "usePersonCreateForm.ts",
              method: "handleSubmit",
              message: "Error submitting person create form",
            });
            onError(t("ERRORS.CREATE_PERSON"));
          }
        }
      },
      [
        existingPersonInfo,
        loggedInAs,
        t,
        personCreate,
        personUpdateAddVisibleTo,
        onError,
        onSuccess,
        trackPostHogException,
      ],
    ),
    defaultValues,
    { schema: personCreateFormSchema, mode: "onBlur" },
  );
};
